import type { ReactElement } from "react";

import { ValueField } from "./contract-fields.js";
import type { FormValues } from "./contract-types.js";

/**
 * ご契約者の連絡先（部署名・郵便番号・ご住所・お電話番号・メールアドレス）。
 * <b>新規申し込みと契約内容の変更で同じ部品</b>。欄の名前は API の <c>Contractor*</c> と同じ。
 * 有償プランでは部署名のほかが必須（Implem.Portal.Backend の docs/features/31）。請求先は持たない。
 */

interface ContractorField {
    readonly name: string;
    readonly label: string;
    readonly autoComplete: string;
    /** 有償プランでも任意の欄（部署名）。 */
    readonly optional?: boolean;
    readonly placeholder?: string;
    readonly inputClassName?: string;
}

const contractorInputs: readonly ContractorField[] = [
    { name: "ContractorDepartment", label: "部署名", autoComplete: "organization-title", optional: true },
    {
        name: "ContractorPostalCode", label: "郵便番号", autoComplete: "postal-code",
        placeholder: "123-4567", inputClassName: "max-w-40",
    },
    { name: "ContractorAddress", label: "ご住所", autoComplete: "street-address" },
    {
        name: "ContractorTel", label: "お電話番号", autoComplete: "tel", inputClassName: "max-w-64",
        placeholder: "03-1234-5678",
    },
    { name: "ContractorEmail", label: "メールアドレス", autoComplete: "email" },
];

export interface ContractorFieldsProps {
    readonly values: FormValues;
    /** 必須の印を付けるか（有償プラン）。無償プランでは全部任意。 */
    readonly required: boolean;
    readonly fieldError: (name: string) => string | undefined;
    readonly onChange: (name: string, value: string) => void;
}

/** ご契約者の連絡先の 5 欄。囲み（見出し・カード）は使う画面が持つ。 */
export function ContractorFields({ values, required, fieldError, onChange }: ContractorFieldsProps): ReactElement {
    return (
        <div className="grid gap-4" data-contractor-contact="">
            {contractorInputs.map((field) => (
                <ValueField
                    key={field.name}
                    label={field.label}
                    name={field.name}
                    autoComplete={field.autoComplete}
                    required={required && field.optional !== true}
                    {...(field.optional === true ? { help: "任意です。" } : {})}
                    {...(field.placeholder === undefined ? {} : { placeholder: field.placeholder })}
                    {...(field.inputClassName === undefined ? {} : { inputClassName: field.inputClassName })}
                    value={values[field.name] ?? ""}
                    onChange={onChange}
                    error={fieldError(field.name)}
                />
            ))}
        </div>
    );
}
